import React, { useEffect, useState, useMemo, useRef, useCallback } from 'react';
import { ExternalLink, MapPin, Calendar, Trophy, Users, Globe, Shield, Heart, ArrowUpRight } from 'lucide-react';
import { getCustomBlockItems } from '../../../../services/customBlockDataService';
import useBlockStyles from '../common/useBlockStyles';

const STYLE_OPTIONS = {
  defaultBackground: 'section',
  defaultHeadingSize: 'large',
  defaultAlignment: 'center',
  defaultPadding: 'normal',
};

// Default categories (Wadim's initiative timeline)
const DEFAULT_CAT = {
  tournament: { icon: Trophy, label: 'Tournament' },
  youth:      { icon: Users, label: 'Youth' },
  federation: { icon: Globe, label: 'Federation' },
  team:       { icon: Shield, label: 'Team' },
  charity:    { icon: Heart, label: 'Charity' },
};

const DEFAULT_LABELS = {
  tournament: ['Tournament', 'Tournaments'],
  youth: ['Youth Program', 'Youth Programs'],
  federation: ['Federation Initiative', 'Federation Initiatives'],
  team: ['Team Milestone', 'Team Milestones'],
  charity: ['Charity Event', 'Charity Events'],
};

const DEFAULT_INTRO = 'Since 2023, Wadim Rosenstein has funded world-class tournaments, sponsored youth across 74 countries, and covered travel for federation players on every continent. These are the receipts.';

const useIsDesktop = () => {
  const [desktop, setDesktop] = useState(() => typeof window !== 'undefined' && window.innerWidth >= 768);
  useEffect(() => {
    const mq = window.matchMedia('(min-width: 768px)');
    const handler = (e) => setDesktop(e.matches);
    mq.addEventListener('change', handler);
    return () => mq.removeEventListener('change', handler);
  }, []);
  return desktop;
};

const useInView = (threshold = 0.12) => {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => { if (e.isIntersecting) { setVisible(true); obs.unobserve(el); } },
      { threshold }
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, [threshold]);
  return [ref, visible];
};

/* ═══════════════════════════════════════════════════════
   YEAR DIVIDER — full-width moment that owns the page
   ═══════════════════════════════════════════════════════ */
const YearDivider = ({ year, itemCount, styles, isFirst }) => {
  const [ref, visible] = useInView();
  return (
    <div
      ref={ref}
      className={`relative ${isFirst ? '' : 'mt-20 md:mt-28'} mb-10 md:mb-14`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(16px)',
        transition: 'all 0.7s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      {/* Horizontal rule with year */}
      <div className="flex items-center gap-6 md:gap-8">
        {/* Left line */}
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, transparent, ${styles.accentColor}40)` }} />

        {/* Year block */}
        <span
          className="text-5xl md:text-7xl font-black tabular-nums leading-none"
          style={{ color: styles.accentColor }}
        >
          {year}
        </span>

        {/* Right line */}
        <div className="flex-1 h-px" style={{ background: `linear-gradient(90deg, ${styles.accentColor}40, transparent)` }} />
      </div>

      {/* Item count below */}
      <p className="text-center mt-3 text-xs tracking-[0.2em] uppercase" style={{ color: styles.mutedColor }}>
        {itemCount} initiative{itemCount !== 1 ? 's' : ''}
      </p>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   TIMELINE CARD — left accent bar, structured interior
   ═══════════════════════════════════════════════════════ */
const TimelineCard = ({ item, isRight, index, styles, categories }) => {
  const cats = categories || DEFAULT_CAT;
  const cat = cats[item.category] || cats[Object.keys(cats)[0]];
  const CatIcon = cat.icon;
  const [ref, visible] = useInView();
  const [hovered, setHovered] = useState(false);
  const isDesktop = useIsDesktop();
  const borderSide = isDesktop ? (isRight ? 'right' : 'left') : 'left';

  return (
    <div
      ref={ref}
      className={`relative flex items-stretch mb-5 md:mb-6 ${
        isRight ? 'md:flex-row' : 'md:flex-row-reverse'
      }`}
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transition: `all 0.55s cubic-bezier(0.16, 1, 0.3, 1) ${index * 0.06}s`,
      }}
    >
      {/* Spacer */}
      <div className="hidden md:block md:w-1/2" />

      {/* Card */}
      <div className={`ml-10 md:ml-0 flex-1 ${isRight ? 'md:w-1/2 md:pl-10' : 'md:w-1/2 md:pr-10'} max-w-full md:max-w-[50%]`}>
        <div
          className="relative rounded-xl overflow-hidden transition-all duration-400 p-5 md:p-6"
          style={{
            backgroundColor: styles.cardBackground,
            borderLeft: borderSide === 'left'
              ? `3px solid ${hovered ? styles.accentColor : `${styles.accentColor}40`}`
              : 'none',
            borderRight: borderSide === 'right'
              ? `3px solid ${hovered ? styles.accentColor : `${styles.accentColor}40`}`
              : 'none',
            boxShadow: hovered
              ? `0 12px 40px rgba(0,0,0,0.25), inset 0 0 0 1px ${styles.accentColor}20`
              : `0 2px 12px rgba(0,0,0,0.12), inset 0 0 0 1px ${styles.borderColor}40`,
            transform: hovered ? 'translateY(-1px)' : 'none',
          }}
          onMouseEnter={() => setHovered(true)}
          onMouseLeave={() => setHovered(false)}
        >
          {/* Title */}
          <h3
            className="text-base md:text-[17px] font-bold leading-snug mb-2"
            style={{ color: styles.headingColor }}
          >
            {item.title}
          </h3>

          {/* Location */}
          {item.location && (
            <div className="flex items-center gap-1.5 mb-3">
              <MapPin size={11} style={{ color: styles.mutedColor }} className="opacity-50 flex-shrink-0" />
              <span className="text-xs" style={{ color: styles.mutedColor }}>
                {item.location}
              </span>
            </div>
          )}

          {/* Description */}
          <p
            className="text-[13px] leading-[1.75]"
            style={{ color: `${styles.bodyColor}bb` }}
          >
            {item.description}
          </p>

          {/* Source link */}
          {item.sourceUrl && (
            <a
              href={item.sourceUrl}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-1 mt-4 text-[11px] font-semibold uppercase tracking-[0.1em] transition-all duration-300 group/link"
              style={{ color: hovered ? styles.accentColor : `${styles.accentColor}90` }}
            >
              Read more
              <ArrowUpRight size={11} className="transition-transform duration-300 group-hover/link:translate-x-0.5 group-hover/link:-translate-y-0.5" />
            </a>
          )}
        </div>
      </div>
    </div>
  );
};

/* ═══════════════════════════════════════════════════════
   MAIN RENDERER
   ═══════════════════════════════════════════════════════ */
const InitiativeTimelineRenderer = ({ block, profileId, theme = {}, staticItems, introText, categories, categoryLabels, sortOrder }) => {
  const { content } = block;
  const { heading } = content;

  const [items, setItems] = useState(staticItems || []);
  const [loading, setLoading] = useState(!staticItems);
  const styles = useBlockStyles(content, theme, STYLE_OPTIONS);
  const cats = categories || DEFAULT_CAT;
  const labels = categoryLabels || DEFAULT_LABELS;
  const intro = introText || DEFAULT_INTRO;

  useEffect(() => {
    if (staticItems) return; // skip fetch when items are passed directly
    const load = async () => {
      if (!profileId) { setLoading(false); return; }
      try {
        const fetched = await getCustomBlockItems(profileId, 'initiative-timeline');
        setItems(fetched);
      } catch (error) {
        console.error('Error loading timeline:', error);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [profileId, staticItems]);

  const groupedByYear = useMemo(() => {
    const groups = {};
    items.forEach(item => {
      const year = item.year || 'Unknown';
      if (!groups[year]) groups[year] = [];
      groups[year].push(item);
    });
    const asc = sortOrder === 'asc';
    return Object.entries(groups).sort(([a], [b]) => {
      const numA = Number(a), numB = Number(b);
      if (!isNaN(numA) && !isNaN(numB)) return asc ? numA - numB : numB - numA;
      return asc ? a.localeCompare(b) : b.localeCompare(a);
    });
  }, [items, sortOrder]);

  if (loading) {
    return (
      <div className={`w-full ${styles.sectionPaddingClass}`} style={{ backgroundColor: 'transparent' }}>
        <div className="max-w-5xl mx-auto flex flex-col items-center py-20 gap-4">
          <div className="w-7 h-7 border-2 border-t-transparent rounded-full animate-spin"
            style={{ borderColor: styles.accentColor, borderTopColor: 'transparent' }} />
        </div>
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className={`w-full ${styles.sectionPaddingClass}`} style={{ backgroundColor: 'transparent' }}>
        <div className="max-w-5xl mx-auto text-center py-16">
          <p className="text-sm" style={{ color: styles.mutedColor }}>No timeline entries yet.</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`w-full ${styles.sectionPaddingClass} overflow-hidden`} style={{ backgroundColor: styles.sectionBackground }}>
      <div className="max-w-5xl mx-auto">

        {/* ── HEADER ── */}
        <div className="text-center mb-12 md:mb-16">
          {heading && (
            <h2
              className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold mb-6"
              style={{ color: styles.headingColor, fontFamily: styles.headingFontStyle }}
            >
              {heading}
            </h2>
          )}

          <p
            className="text-base sm:text-lg leading-relaxed max-w-2xl mx-auto mb-10 opacity-90"
            style={{ color: styles.bodyColor, fontFamily: styles.bodyFontStyle }}
          >
            {intro}
          </p>

          {/* At a glance */}
          <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm" style={{ color: styles.mutedColor }}>
            {Object.entries((() => { const c = {}; items.forEach(i => { c[i.category] = (c[i.category] || 0) + 1; }); return c; })())
              .sort(([,a],[,b]) => b - a)
              .map(([key, count]) => {
                const c = cats[key];
                if (!c) return null;
                const label = labels[key] ? labels[key][count === 1 ? 0 : 1] : c.label;
                return (
                  <span key={key}>
                    <span style={{ color: styles.accentColor }} className="font-bold">{count}</span>
                    {' '}{label}
                  </span>
                );
              })
              .reduce((acc, el, i) => {
                if (i > 0) acc.push(
                  <span key={`sep-${i}`} className="opacity-30">·</span>
                );
                acc.push(el);
                return acc;
              }, [])}
          </div>
        </div>

        {/* ── TIMELINE ── */}
        <div className="relative">
          {/* Spine */}
          <div
            className="absolute top-0 bottom-0 left-[18px] md:left-1/2 md:-translate-x-px"
            style={{
              width: '1px',
              background: `linear-gradient(180deg, transparent 0%, ${styles.accentColor}30 4%, ${styles.accentColor}25 96%, transparent 100%)`,
            }}
          />

          {groupedByYear.map(([year, yearItems], yearIdx) => (
            <div key={year}>
              <YearDivider
                year={year}
                itemCount={yearItems.length}
                styles={styles}
                isFirst={yearIdx === 0}
              />
              {yearItems.map((item, idx) => (
                <TimelineCard
                  key={item.id}
                  item={item}
                  isRight={idx % 2 === 0}
                  index={idx}
                  styles={styles}
                  categories={cats}
                />
              ))}
            </div>
          ))}

        </div>
      </div>
    </div>
  );
};

export default InitiativeTimelineRenderer;
